/**
 * Occupational Health.
 *
 * A deliberately small module: only the facts a factory's EHS MIS reports. There
 * is no diagnosis, findings or restrictions field — the server does not collect
 * them, so there is nothing here to show or hide.
 *
 * Three things the page has to get right:
 *
 *   INSIGHT FIRST      A plant head opens this to find out what to chase, not to
 *                      browse records: ranked findings and the department carrying
 *                      the overdue load come before any table.
 *
 *   BULK ENTRY         The data lives in HR's spreadsheet. Download template,
 *                      fill, upload — the forms exist for corrections, not for
 *                      loading a plant's year of examinations.
 *
 *   TWO LEVELS         Aggregates need only reporting access. The per-person lists
 *                      need the dedicated occupational_health grant; hiding the
 *                      tabs without it is courtesy, and the server enforces it and
 *                      audits every read either way.
 *
 * Suppressed cells arrive as strings ("<5", or "—" when the whole group is too
 * small for any outcome to be published) and are rendered verbatim — coercing them
 * to numbers would undo the suppression.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, ArrowLeft, CalendarClock, Info, Loader2, Lock,
  Plus, RefreshCw, ShieldCheck, Stethoscope, Trash2, X, Download, Sparkles,
  Upload, Users,
} from 'lucide-react';
import TabNavigation from '../../components/TabNavigation';
import { useSite } from '../../context/SiteContext';
import { useAlert } from '../../context/AlertContext';
import {
  EXAM_TYPE_LABELS, FITNESS_LABELS, createExamination, createIllnessCase,
  deleteExamination, deleteIllnessCase, downloadRecordTemplate,
  fetchExaminations, fetchHealthInsights, fetchHealthMeta, fetchHealthSummary,
  fetchIllnessCases, fetchWorklist, importRecords, isSuppressed,
  updateExamination, updateIllnessCase,
} from '../../services/occupationalHealthApi';
import { saveBlob } from '../../services/ehsMisApi';

const FITNESS_TONE = {
  fit: 'bg-emerald-100 text-emerald-800',
  fit_with_restrictions: 'bg-amber-100 text-amber-800',
  temporarily_unfit: 'bg-rose-100 text-rose-800',
  unfit: 'bg-rose-100 text-rose-800',
  pending: 'bg-gray-100 text-gray-600',
};

const EMPTY_EXAM = {
  employee_code: '', employee_name: '', department: '', exam_type: 'periodic',
  hazard_category: '', exam_date: '', next_due_date: '', provider: '',
  fitness_status: 'fit', referral_required: false, follow_up_date: '', notes: '',
};

const EMPTY_CASE = {
  employee_code: '', employee_name: '', department: '', case_date: '',
  illness_category: '', work_related: true, notifiable: false,
  reported_to_authority_date: '', days_lost: '', restricted_duty_days: '',
  status: 'open', closed_date: '', capa_id: '', notes: '',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Render a value that may have been withheld.
 * A suppressed cell arrives as a string and is printed as-is; a real number is
 * formatted. N/A means the figure could not be computed at all.
 */
const Cell = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-gray-400">N/A</span>;
  if (isSuppressed(value)) {
    return (
      <span className="text-gray-500" title="Withheld: too few people to report without identifying someone">
        {value}
      </span>
    );
  }
  return <span>{value}</span>;
};

const SummaryCard = ({ label, value, tone, hint }) => {
  const missing = value === null || value === undefined;
  const alarming = !missing && !isSuppressed(value) && Number(value) > 0 && tone;
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white">
      <div className="text-xs text-gray-500 leading-tight">{label}</div>
      <div className={`text-2xl font-semibold mt-1 tabular-nums ${missing
        ? 'text-gray-400' : (alarming ? tone : 'text-gray-900')}`}>
        {missing ? 'N/A' : value}
      </div>
      {hint && <div className="text-[11px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
};

const Modal = ({ title, subtitle, onClose, children, footer }) => (
  <div className="fixed inset-0 bg-black/40 z-40 flex items-start justify-center overflow-y-auto py-10">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4">
      <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <button onClick={onClose} aria-label="Close"
                className="text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="px-5 py-4">{children}</div>
      {footer && (
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">{footer}</div>
      )}
    </div>
  </div>
);

const Field = ({ label, hint, children, required }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-600 mb-1">
      {label}{required && <span className="text-rose-600"> *</span>}
    </span>
    {children}
    {hint && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
  </label>
);

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm';

/** Ranked findings: what this factory should chase, worst first. */
const HealthFindings = ({ insights }) => {
  const findings = insights?.findings || [];
  if (!findings.length) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5
                      flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-emerald-900">
          Nothing overdue, unreported or awaiting an outcome on the records held.
        </p>
      </div>
    );
  }
  const chip = { critical: 'bg-rose-600 text-white', warning: 'bg-amber-500 text-white',
                 watch: 'bg-slate-400 text-white' };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-5">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-blue-600" />
        <h2 className="font-semibold text-gray-900">What to act on at this factory</h2>
        <span className="text-xs text-gray-500">worst first</span>
      </div>
      <ul className="divide-y divide-gray-100">
        {findings.map((f, i) => (
          <li key={i} className="px-5 py-2.5 flex items-start gap-3">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold mt-0.5
                              whitespace-nowrap ${chip[f.severity] || chip.watch}`}>
              {f.severity === 'critical' ? 'Critical'
                : f.severity === 'warning' ? 'Action' : 'Watch'}
            </span>
            <span className="flex-1">
              <span className="text-sm text-gray-900">{f.title}</span>
              <span className="block text-xs text-gray-500">{f.detail}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Surveillance load per department — the unit a plant head can actually chase. */
const DepartmentLoad = ({ insights }) => {
  const rows = insights?.by_department || [];
  if (!rows.length) return null;
  const worst = Math.max(1, ...rows.map((r) => r.overdue));
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-gray-500" />
        <h3 className="font-semibold text-gray-900">By department</h3>
        <span className="text-[11px] text-gray-500">
          overdue examinations, then people under surveillance
        </span>
      </div>
      <table className="min-w-full text-sm">
        <thead className="text-xs uppercase text-gray-500">
          <tr>
            <th className="py-1 text-left">Department</th>
            <th className="py-1 text-right">People</th>
            <th className="py-1 text-right">Overdue</th>
            <th className="py-1 text-right">Constrained</th>
            <th className="py-1 w-32" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={r.department}>
              <td className="py-1.5 text-gray-900">{r.department}</td>
              <td className="py-1.5 text-right tabular-nums"><Cell value={r.people} /></td>
              <td className={`py-1.5 text-right tabular-nums ${r.overdue
                ? 'text-rose-700 font-medium' : 'text-gray-500'}`}>{r.overdue}</td>
              <td className="py-1.5 text-right tabular-nums">
                <Cell value={r.constrained} />
              </td>
              <td className="py-1.5 pl-3">
                <span className="block h-2 bg-gray-100 rounded">
                  <span className="block h-2 bg-rose-500 rounded"
                        style={{ width: `${(r.overdue / worst) * 100}%` }} />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const OccupationalHealth = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showError, showSuccess, confirm } = useAlert();
  const { activeSiteId, availableSites } = useSite();

  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState('programme');
  const [summary, setSummary] = useState({});
  const [breakdowns, setBreakdowns] = useState({ hazard: [], illness: [] });
  const [worklist, setWorklist] = useState([]);
  const [exams, setExams] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [examForm, setExamForm] = useState(null);
  const [caseForm, setCaseForm] = useState(null);
  // The insight layer: ranked findings and the department load.
  const [insights, setInsights] = useState(null);
  const [busyKind, setBusyKind] = useState(null);
  // Which template the Template / Import buttons act on.
  const [importKind, setImportKind] = useState('examinations');

  const siteId = activeSiteId || '';
  const needsSiteChoice = !siteId && availableSites.length > 1;
  const access = meta?.record_access || {};
  const canRead = Boolean(access.read);
  const canWrite = Boolean(access.create);

  useEffect(() => {
    const controller = new AbortController();
    fetchHealthMeta({ signal: controller.signal })
      .then(setMeta)
      .catch((err) => {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
        setMeta({});
      });
    return () => controller.abort();
  }, []);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const [data, ins] = await Promise.all([
        fetchHealthSummary({ siteId: siteId || undefined }, { signal }),
        // Additive: a failure here must not cost the page its figures.
        fetchHealthInsights({ siteId: siteId || undefined }, { signal })
          .catch(() => null),
      ]);
      setSummary(data.summary || {});
      setBreakdowns({
        hazard: data.fitness_by_hazard || [],
        illness: data.illness_by_category || [],
      });
      setInsights(ins);
      if (canRead) {
        const [wl, ex, ic] = await Promise.all([
          fetchWorklist({ siteId: siteId || undefined }, { signal }),
          fetchExaminations({ siteId: siteId || undefined }, { signal }),
          fetchIllnessCases({ siteId: siteId || undefined }, { signal }),
        ]);
        setWorklist(wl);
        setExams(ex);
        setCases(ic);
      }
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      const status = err?.response?.status;
      setError(status === 403
        ? 'You do not have permission to view occupational health data.'
        : err?.response?.data?.detail || 'Could not load occupational health data.');
    } finally {
      setLoading(false);
    }
  }, [siteId, canRead]);

  useEffect(() => {
    if (needsSiteChoice || meta === null) return undefined;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, needsSiteChoice, meta]);

  const tabs = useMemo(() => ([
    { id: 'programme', label: 'Programme', Icon: Activity, clinical: false },
    { id: 'worklist', label: 'Due & overdue', Icon: CalendarClock, clinical: true },
    { id: 'exams', label: 'Examinations', Icon: Stethoscope, clinical: true },
    { id: 'cases', label: 'Illness cases', Icon: ShieldCheck, clinical: true },
  ].filter((t) => !t.clinical || canRead)), [canRead]);

  const numeric = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

  /** Hand HR the exact columns the import expects. */
  const getTemplate = async (kind) => {
    setBusyKind(`template-${kind}`);
    try {
      const { blob, filename } = await downloadRecordTemplate({ kind });
      saveBlob(blob, filename);
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not download the template.');
    } finally {
      setBusyKind(null);
    }
  };

  /**
   * Upload a filled template. The server validates the whole file and writes
   * nothing if any row is wrong, so a rejection is safe — we surface its row-level
   * errors rather than a generic failure, because that is what gets the file fixed.
   */
  const uploadFilled = async (kind, file) => {
    if (!file) return;
    setBusyKind(`import-${kind}`);
    try {
      const res = await importRecords({ siteId, kind, file });
      showSuccess(`${res.imported} ${kind === 'illness' ? 'illness case' : 'examination'}`
        + `${res.imported === 1 ? '' : 's'} imported.`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.errors) {
        showError(`${detail.message}. First problems: `
          + detail.errors.slice(0, 3).join(' · '));
      } else {
        showError(detail || 'Could not import the file.');
      }
    } finally {
      setBusyKind(null);
    }
  };

  const saveExam = async () => {
    const f = examForm;
    if (!f?.exam_date) { showError('An examination date is required.'); return; }
    setSaving(true);
    try {
      const body = {
        site_id: siteId || undefined,
        employee_code: f.employee_code || null,
        employee_name: f.employee_name || null,
        department: f.department || null,
        exam_type: f.exam_type,
        hazard_category: f.hazard_category || null,
        exam_date: f.exam_date,
        next_due_date: f.next_due_date || null,
        provider: f.provider || null,
        fitness_status: f.fitness_status,
        referral_required: f.referral_required,
        follow_up_date: f.follow_up_date || null,
        notes: f.notes || null,
      };
      if (f.id) await updateExamination(f.id, body);
      else await createExamination(body);
      setExamForm(null);
      showSuccess('Examination recorded.');
      await load();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not record the examination.');
    } finally {
      setSaving(false);
    }
  };

  const saveCase = async () => {
    const f = caseForm;
    if (!f?.case_date) { showError('A case date is required.'); return; }
    if (f.status === 'closed' && !f.closed_date) {
      showError('A closed case needs a closed date.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        site_id: siteId || undefined,
        employee_code: f.employee_code || null,
        employee_name: f.employee_name || null,
        department: f.department || null,
        case_date: f.case_date,
        illness_category: f.illness_category || null,
        work_related: f.work_related,
        notifiable: f.notifiable,
        reported_to_authority_date: f.reported_to_authority_date || null,
        days_lost: numeric(f.days_lost),
        restricted_duty_days: numeric(f.restricted_duty_days),
        status: f.status,
        closed_date: f.closed_date || null,
        capa_id: f.capa_id || null,
        notes: f.notes || null,
      };
      if (f.id) await updateIllnessCase(f.id, body);
      else await createIllnessCase(body);
      setCaseForm(null);
      showSuccess('Illness case recorded.');
      await load();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not record the case.');
    } finally {
      setSaving(false);
    }
  };

  const removeExam = async (row) => {
    const ok = await confirm(
      'Delete this examination?',
      `The record for ${row.employee_name || row.employee_code || 'this person'} `
      + `on ${fmtDate(row.exam_date)} will be removed from the register. `
      + 'The row is retained for audit.',
      { confirmLabel: 'Delete', destructive: true },
    );
    if (!ok) return;
    try {
      await deleteExamination(row.id, { siteId: siteId || undefined });
      showSuccess('Examination deleted.');
      await load();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not delete the examination.');
    }
  };

  const removeCase = async (row) => {
    const ok = await confirm(
      'Delete this illness case?',
      'The case will be removed from the register. The row is retained for audit.',
      { confirmLabel: 'Delete', destructive: true },
    );
    if (!ok) return;
    try {
      await deleteIllnessCase(row.id, { siteId: siteId || undefined });
      showSuccess('Case deleted.');
      await load();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not delete the case.');
    }
  };

  const openEditExam = (row) => setExamForm({
    id: row.id,
    employee_code: row.employee_code || '',
    employee_name: row.employee_name || '',
    department: row.department || '',
    exam_type: row.exam_type || 'periodic',
    hazard_category: row.hazard_category || '',
    exam_date: row.exam_date || '',
    next_due_date: row.next_due_date || '',
    provider: row.provider || '',
    fitness_status: row.fitness_status || 'fit',
    referral_required: Boolean(row.referral_required),
    follow_up_date: row.follow_up_date || '',
    notes: row.notes || '',
  });

  const openEditCase = (row) => setCaseForm({
    id: row.id,
    employee_code: row.employee_code || '',
    employee_name: row.employee_name || '',
    department: row.department || '',
    case_date: row.case_date || '',
    illness_category: row.illness_category || '',
    work_related: Boolean(row.work_related),
    notifiable: Boolean(row.notifiable),
    reported_to_authority_date: row.reported_to_authority_date || '',
    days_lost: row.days_lost ?? '',
    restricted_duty_days: row.restricted_duty_days ?? '',
    status: row.status || 'open',
    closed_date: row.closed_date || '',
    capa_id: row.capa_id || '',
    notes: row.notes || '',
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button onClick={() => navigate('/safety')}
                  className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Safety
          </button>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Occupational Health</h1>
              <p className="text-gray-600 mt-1">
                Health surveillance coverage, fitness outcomes and work-related
                illness — only what the EHS MIS reports.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => load()} disabled={loading || needsSiteChoice}
                      className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300
                                 rounded-lg text-sm text-gray-700 hover:bg-gray-50
                                 disabled:opacity-50">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              {canWrite && (
                <>
                  {/* Bulk entry leads: this data arrives from HR as a spreadsheet,
                      and the single-record forms are for corrections. */}
                  <button onClick={() => getTemplate(importKind)}
                          disabled={Boolean(busyKind)}
                          className="inline-flex items-center gap-2 px-3 py-2 border
                                     border-gray-300 rounded-lg text-sm text-gray-700
                                     hover:bg-gray-50 disabled:opacity-50">
                    {busyKind === `template-${importKind}`
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Download className="h-4 w-4" />}
                    Template
                  </button>
                  <label className={`inline-flex items-center gap-2 px-3 py-2 border
                                     border-gray-300 rounded-lg text-sm text-gray-700
                                     hover:bg-gray-50 cursor-pointer
                                     ${busyKind || needsSiteChoice ? 'opacity-50 pointer-events-none' : ''}`}>
                    {busyKind === `import-${importKind}`
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Upload className="h-4 w-4" />}
                    Import filled file
                    <input type="file" accept=".csv" className="hidden"
                           onChange={(e) => {
                             const file = e.target.files?.[0];
                             e.target.value = '';   // allow re-picking the same file
                             uploadFilled(importKind, file);
                           }} />
                  </label>
                  <select value={importKind}
                          onChange={(e) => setImportKind(e.target.value)}
                          title="Which template to download or import"
                          className="px-2 py-2 border border-gray-300 rounded-lg text-sm
                                     text-gray-700">
                    <option value="examinations">Examinations</option>
                    <option value="illness">Illness cases</option>
                  </select>
                  <button onClick={() => setExamForm({ ...EMPTY_EXAM })}
                          disabled={needsSiteChoice}
                          title="Add or correct a single record"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600
                                     text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                    <Plus className="h-4 w-4" />
                    Examination
                  </button>
                  <button onClick={() => setCaseForm({ ...EMPTY_CASE })}
                          disabled={needsSiteChoice}
                          className="inline-flex items-center gap-2 px-4 py-2 border
                                     border-gray-300 rounded-lg text-sm text-gray-700
                                     hover:bg-gray-50 disabled:opacity-50">
                    <Plus className="h-4 w-4" />
                    Illness case
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {needsSiteChoice && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Pick a site in the header bar. Health surveillance is run per site.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {meta && !canRead && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Lock className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700">
              You are seeing programme aggregates only. The per-person records
              require the <span className="font-medium">occupational health</span>{' '}
              permission, granted separately from safety access — a name against a
              fitness outcome is still personal health information.
            </p>
          </div>
        )}

        {!needsSiteChoice && (
          <>
            <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
              {tabs.map(({ id, label, Icon }) => (
                <button key={id} onClick={() => setTab(id)}
                        className={`inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2
                                    -mb-px ${tab === id
                                      ? 'border-blue-600 text-blue-700 font-medium'
                                      : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>

            {tab === 'programme' && (
              <>
                {/* Insight before data: a plant head opens this to find out what
                    to chase, not to browse a table. */}
                <HealthFindings insights={insights} />

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                  <SummaryCard label="Under surveillance"
                               value={summary.people_under_surveillance} />
                  <SummaryCard label="Coverage"
                               value={summary.coverage_rate === null
                                 || summary.coverage_rate === undefined
                                 ? null : `${summary.coverage_rate}%`}
                               hint="Of those scheduled" />
                  <SummaryCard label="Overdue" value={summary.overdue}
                               tone="text-rose-700" />
                  <SummaryCard label="Due in 30 days" value={summary.due_soon}
                               tone="text-amber-700" />
                  <SummaryCard label="With restrictions"
                               value={summary.fit_with_restrictions}
                               tone="text-amber-700" />
                  <SummaryCard label="Awaiting outcome" value={summary.pending_outcome}
                               tone="text-amber-700" hint="Not counted as fit" />
                </div>

                <DepartmentLoad insights={insights} />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <h2 className="font-semibold text-gray-900">
                        Fitness by hazard surveilled
                      </h2>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Cells below {meta?.suppression_threshold || 5} people are
                        withheld so an individual cannot be identified.
                      </p>
                    </div>
                    {breakdowns.hazard.length === 0 ? (
                      <div className="p-6 text-sm text-gray-500 text-center">
                        No examinations recorded for this site.
                      </div>
                    ) : (
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                          <tr>
                            <th className="px-4 py-2 text-left">Hazard</th>
                            <th className="px-4 py-2 text-right">People</th>
                            <th className="px-4 py-2 text-right">Fit</th>
                            <th className="px-4 py-2 text-right">Constrained</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {breakdowns.hazard.map((r) => (
                            <tr key={r.category}>
                              <td className="px-4 py-2 text-gray-900">{r.category}</td>
                              <td className="px-4 py-2 text-right tabular-nums">
                                <Cell value={r.people} />
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums">
                                <Cell value={r.fit} />
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums">
                                <Cell value={r.constrained} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <h2 className="font-semibold text-gray-900">
                        Work-related illness by category
                      </h2>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Days lost is a magnitude rather than a headcount, so it is
                        not suppressed.
                      </p>
                    </div>
                    {breakdowns.illness.length === 0 ? (
                      <div className="p-6 text-sm text-gray-500 text-center">
                        No work-related illness cases recorded.
                      </div>
                    ) : (
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                          <tr>
                            <th className="px-4 py-2 text-left">Category</th>
                            <th className="px-4 py-2 text-right">Cases</th>
                            <th className="px-4 py-2 text-right">Notifiable</th>
                            <th className="px-4 py-2 text-right">Days lost</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {breakdowns.illness.map((r) => (
                            <tr key={r.category}>
                              <td className="px-4 py-2 text-gray-900">{r.category}</td>
                              <td className="px-4 py-2 text-right tabular-nums">
                                <Cell value={r.cases} />
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums">
                                <Cell value={r.notifiable} />
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums">
                                <Cell value={r.days_lost} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </>
            )}

            {tab === 'worklist' && canRead && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                  <h2 className="font-semibold text-gray-900">Due and overdue</h2>
                  <span className="text-[11px] text-gray-500">
                    Latest examination per person, within 60 days of falling due.
                  </span>
                  {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400 ml-auto" />}
                </div>
                {worklist.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-500">
                    Nobody is due in the next 60 days.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-4 py-2 text-left">Employee</th>
                          <th className="px-4 py-2 text-left">Department</th>
                          <th className="px-4 py-2 text-left">Hazard</th>
                          <th className="px-4 py-2 text-left">Last exam</th>
                          <th className="px-4 py-2 text-left">Next due</th>
                          <th className="px-4 py-2 text-left">Fitness</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {worklist.map((r) => (
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <div className="text-gray-900">{r.employee_name || '—'}</div>
                              <div className="text-[11px] text-gray-500">
                                {r.employee_code || '—'}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-gray-700">{r.department || '—'}</td>
                            <td className="px-4 py-2 text-gray-700">
                              {r.hazard_category || '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                              {fmtDate(r.exam_date)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <span className={r.overdue ? 'text-rose-700 font-medium' : 'text-gray-900'}>
                                {fmtDate(r.next_due_date)}
                              </span>
                              {r.overdue && (
                                <span className="ml-2 px-1.5 py-0.5 rounded bg-rose-100
                                                 text-rose-800 text-[10px]">Overdue</span>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-0.5 rounded text-[11px] font-medium
                                                ${FITNESS_TONE[r.fitness_status] || FITNESS_TONE.pending}`}>
                                {FITNESS_LABELS[r.fitness_status] || r.fitness_status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {tab === 'exams' && canRead && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                  <Lock className="h-4 w-4 text-slate-400" />
                  <h2 className="font-semibold text-gray-900">Examinations</h2>
                  <span className="text-[11px] text-gray-500">
                    Clinical records. Every view of this list is audit-logged.
                  </span>
                </div>
                {exams.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-500">
                    No examinations recorded for this site.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-4 py-2 text-left">Employee</th>
                          <th className="px-4 py-2 text-left">Type</th>
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-left">Next due</th>
                          <th className="px-4 py-2 text-left">Fitness</th>
                          <th className="px-4 py-2 text-left">Referral</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {exams.map((r) => (
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <div className="text-gray-900">{r.employee_name || '—'}</div>
                              <div className="text-[11px] text-gray-500">
                                {[r.employee_code, r.hazard_category].filter(Boolean).join(' · ') || '—'}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-gray-700">
                              {EXAM_TYPE_LABELS[r.exam_type] || r.exam_type}
                            </td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                              {fmtDate(r.exam_date)}
                            </td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                              {fmtDate(r.next_due_date)}
                            </td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-0.5 rounded text-[11px] font-medium
                                                ${FITNESS_TONE[r.fitness_status] || FITNESS_TONE.pending}`}>
                                {FITNESS_LABELS[r.fitness_status] || r.fitness_status}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-xs">
                              {r.referral_required
                                ? <span className="px-1.5 py-0.5 rounded bg-amber-100
                                                   text-amber-800">Referred</span>
                                : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">
                              {access.update && (
                                <button onClick={() => openEditExam(r)}
                                        className="text-blue-600 hover:text-blue-800 text-xs
                                                   font-medium mr-3">
                                  Edit
                                </button>
                              )}
                              {access.delete && (
                                <button onClick={() => removeExam(r)} title="Delete"
                                        className="text-gray-400 hover:text-rose-600">
                                  <Trash2 className="h-4 w-4 inline" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {tab === 'cases' && canRead && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                  <Lock className="h-4 w-4 text-slate-400" />
                  <h2 className="font-semibold text-gray-900">Occupational illness cases</h2>
                  <span className="text-[11px] text-gray-500">
                    Clinical records. Every view of this list is audit-logged.
                  </span>
                </div>
                {cases.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-500">
                    No illness cases recorded for this site.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-4 py-2 text-left">Employee</th>
                          <th className="px-4 py-2 text-left">Category</th>
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-left">Work-related</th>
                          <th className="px-4 py-2 text-left">Notifiable</th>
                          <th className="px-4 py-2 text-right">Days lost</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {cases.map((r) => (
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <div className="text-gray-900">{r.employee_name || '—'}</div>
                              <div className="text-[11px] text-gray-500">
                                {r.employee_code || '—'}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-gray-700">
                              {r.illness_category || '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                              {fmtDate(r.case_date)}
                            </td>
                            <td className="px-4 py-2 text-gray-700">
                              {r.work_related ? 'Yes' : 'No'}
                            </td>
                            <td className="px-4 py-2">
                              {r.notifiable ? (
                                <span className={`px-2 py-0.5 rounded text-[11px] font-medium
                                  ${r.reported_to_authority_date
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-rose-100 text-rose-800'}`}>
                                  {r.reported_to_authority_date ? 'Reported' : 'Not reported'}
                                </span>
                              ) : <span className="text-gray-400 text-xs">No</span>}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                              {r.days_lost ?? '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-700 capitalize">{r.status}</td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">
                              {access.update && (
                                <button onClick={() => openEditCase(r)}
                                        className="text-blue-600 hover:text-blue-800 text-xs
                                                   font-medium mr-3">
                                  Edit
                                </button>
                              )}
                              {access.delete && (
                                <button onClick={() => removeCase(r)} title="Delete"
                                        className="text-gray-400 hover:text-rose-600">
                                  <Trash2 className="h-4 w-4 inline" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {examForm && (
        <Modal
          title={examForm.id ? 'Edit examination' : 'Record examination'}
          subtitle="Only what the EHS MIS reports: who, when, next due, and the fitness outcome."
          onClose={() => setExamForm(null)}
          footer={(
            <>
              <button onClick={() => setExamForm(null)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700">
                Cancel
              </button>
              <button onClick={saveExam} disabled={saving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white
                                 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </button>
            </>
          )}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Employee name">
              <input className={inputCls} value={examForm.employee_name}
                     onChange={(e) => setExamForm((f) => ({ ...f, employee_name: e.target.value }))} />
            </Field>
            <Field label="Employee code"
                   hint="Used to group a person's examinations, so status follows the latest one.">
              <input className={inputCls} value={examForm.employee_code}
                     onChange={(e) => setExamForm((f) => ({ ...f, employee_code: e.target.value }))} />
            </Field>
            <Field label="Department">
              <input className={inputCls} value={examForm.department}
                     onChange={(e) => setExamForm((f) => ({ ...f, department: e.target.value }))} />
            </Field>
            <Field label="Hazard surveilled" hint="e.g. Noise, Chemical, Dust, Heat.">
              <input className={inputCls} value={examForm.hazard_category}
                     onChange={(e) => setExamForm((f) => ({ ...f, hazard_category: e.target.value }))} />
            </Field>
            <Field label="Examination type" required>
              <select className={inputCls} value={examForm.exam_type}
                      onChange={(e) => setExamForm((f) => ({ ...f, exam_type: e.target.value }))}>
                {(meta?.exam_types || Object.keys(EXAM_TYPE_LABELS)).map((t) => (
                  <option key={t} value={t}>{EXAM_TYPE_LABELS[t] || t}</option>
                ))}
              </select>
            </Field>
            <Field label="Fitness outcome" required
                   hint="Leave as awaiting outcome until it is known — it is never counted as fit.">
              <select className={inputCls} value={examForm.fitness_status}
                      onChange={(e) => setExamForm((f) => ({ ...f, fitness_status: e.target.value }))}>
                {(meta?.fitness_statuses || Object.keys(FITNESS_LABELS)).map((s) => (
                  <option key={s} value={s}>{FITNESS_LABELS[s] || s}</option>
                ))}
              </select>
            </Field>
            <Field label="Examination date" required>
              <input type="date" className={inputCls} value={examForm.exam_date}
                     onChange={(e) => setExamForm((f) => ({ ...f, exam_date: e.target.value }))} />
            </Field>
            <Field label="Next due date"
                   hint="Leave blank if this examination does not recur. Without it the person is not tracked as due.">
              <input type="date" className={inputCls} value={examForm.next_due_date}
                     onChange={(e) => setExamForm((f) => ({ ...f, next_due_date: e.target.value }))} />
            </Field>
            <Field label="Provider">
              <input className={inputCls} value={examForm.provider}
                     onChange={(e) => setExamForm((f) => ({ ...f, provider: e.target.value }))} />
            </Field>
            <div className="flex items-end gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={examForm.referral_required}
                       onChange={(e) => setExamForm((f) => ({ ...f, referral_required: e.target.checked }))} />
                Referral required
              </label>
            </div>
            <Field label="Follow-up date">
              <input type="date" className={inputCls} value={examForm.follow_up_date}
                     onChange={(e) => setExamForm((f) => ({ ...f, follow_up_date: e.target.value }))} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Scheduling notes"
                     hint="Logistics only. This module does not record clinical detail — no diagnosis, findings or restrictions.">
                <textarea className={inputCls} rows="2" value={examForm.notes}
                          onChange={(e) => setExamForm((f) => ({ ...f, notes: e.target.value }))} />
              </Field>
            </div>
          </div>
        </Modal>
      )}

      {caseForm && (
        <Modal
          title={caseForm.id ? 'Edit illness case' : 'Record illness case'}
          subtitle="Category, work-relatedness, notification and impact. No diagnosis is recorded."
          onClose={() => setCaseForm(null)}
          footer={(
            <>
              <button onClick={() => setCaseForm(null)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700">
                Cancel
              </button>
              <button onClick={saveCase} disabled={saving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white
                                 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </button>
            </>
          )}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Employee name">
              <input className={inputCls} value={caseForm.employee_name}
                     onChange={(e) => setCaseForm((f) => ({ ...f, employee_name: e.target.value }))} />
            </Field>
            <Field label="Employee code">
              <input className={inputCls} value={caseForm.employee_code}
                     onChange={(e) => setCaseForm((f) => ({ ...f, employee_code: e.target.value }))} />
            </Field>
            <Field label="Department">
              <input className={inputCls} value={caseForm.department}
                     onChange={(e) => setCaseForm((f) => ({ ...f, department: e.target.value }))} />
            </Field>
            <Field label="Illness category">
              <input className={inputCls} value={caseForm.illness_category}
                     list="oh-illness-categories"
                     onChange={(e) => setCaseForm((f) => ({ ...f, illness_category: e.target.value }))} />
              <datalist id="oh-illness-categories">
                {(meta?.illness_categories || []).map((c) => <option key={c} value={c} />)}
              </datalist>
            </Field>
            <Field label="Case date" required>
              <input type="date" className={inputCls} value={caseForm.case_date}
                     onChange={(e) => setCaseForm((f) => ({ ...f, case_date: e.target.value }))} />
            </Field>
            <Field label="Status">
              <select className={inputCls} value={caseForm.status}
                      onChange={(e) => setCaseForm((f) => ({ ...f, status: e.target.value }))}>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
            </Field>
            {caseForm.status === 'closed' && (
              <Field label="Closed date" required>
                <input type="date" className={inputCls} value={caseForm.closed_date}
                       onChange={(e) => setCaseForm((f) => ({ ...f, closed_date: e.target.value }))} />
              </Field>
            )}
            <Field label="Days lost">
              <input type="number" min="0" className={inputCls} value={caseForm.days_lost}
                     onChange={(e) => setCaseForm((f) => ({ ...f, days_lost: e.target.value }))} />
            </Field>
            <Field label="Restricted duty days">
              <input type="number" min="0" className={inputCls}
                     value={caseForm.restricted_duty_days}
                     onChange={(e) => setCaseForm((f) => ({ ...f, restricted_duty_days: e.target.value }))} />
            </Field>
            <div className="flex items-end gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={caseForm.work_related}
                       onChange={(e) => setCaseForm((f) => ({ ...f, work_related: e.target.checked }))} />
                Work-related
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={caseForm.notifiable}
                       onChange={(e) => setCaseForm((f) => ({ ...f, notifiable: e.target.checked }))} />
                Notifiable
              </label>
            </div>
            {caseForm.notifiable && (
              <Field label="Reported to authority on"
                     hint="A notifiable case with no report date is reported as a statutory exposure.">
                <input type="date" className={inputCls}
                       value={caseForm.reported_to_authority_date}
                       onChange={(e) => setCaseForm((f) => ({ ...f, reported_to_authority_date: e.target.value }))} />
              </Field>
            )}
            <Field label="CAPA"
                   hint="Link the corrective action so the cause is tracked to closure.">
              <input className={inputCls} value={caseForm.capa_id}
                     onChange={(e) => setCaseForm((f) => ({ ...f, capa_id: e.target.value }))}
                     placeholder="CAPA id" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Notes"
                     hint="This module records the category and the impact, never the diagnosis.">
                <textarea className={inputCls} rows="2" value={caseForm.notes}
                          onChange={(e) => setCaseForm((f) => ({ ...f, notes: e.target.value }))} />
              </Field>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default OccupationalHealth;
